import React, {Component, ReactNode} from 'react'
import {
    DialogContent,
    DialogTitle,
    Table,
    DialogActions,
    TableBody,
    TableRow,
    TableCell,
    InputAdornment,
    Box,
} from '@mui/material';
import {
    HeadingAreaMessageComponent,
    HeadingAreaMessageParams,
} from '../../../shared/pageBaseComponents/HeadingAreaMessageComponent'
import PPNumericField from '../../../shared/forms/PPNumericField'
import {HKConstants} from '../../../../HKConstants'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../../../shared/hoc/withAdminPageTemplate'
import {AdminPageHeadingArea} from '../../../shared/pageBaseComponents/AdminPageHeadingArea'
import styled from 'styled-components'
import {AdminPrimaryButton} from '../../../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../../../styledComponents/adminPage/Links'
import {
    MESSAGE_TYPE,
    PAYMENT_TYPE, RENTAL_TYPE,
    RESERVATION_TIME_UNIT_MINUTES_NOT0,
    UNIT_TIME,
    VALID_OR_NOT,
} from '../../../../CodeMaster'
import {Nullable} from '../../../../app/TypeHelpers'
import PPTextField from '../../../shared/forms/PPTextField'
import PPCheckboxGroup from '../../../shared/forms/PPCheckboxGroup'
import {Messages} from '../../../../Messages'
import PPSelect from '../../../shared/forms/PPSelect'
import {createUUID, numberOrNull} from '../../../../stringHelper/stringHelpers'
import {LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents'
import EventEmitterExAdmin from '../../../shared/pageBaseComponents/EventEmitterExAdmin'
import {HttpError} from '../../../../network/errors/HttpError'
import {ErrorResponseBody} from '../../../../network/response/ErrorResponseBody'
import {HttpStatusCode} from '../../../../network/Constants'
import {RequestError} from '../../../../network/response/RequestError'
import {RequestItemError} from '../../../../network/response/RequestItemError'
import {StyledTableCaption, TableRowName} from '../../../../styledComponents/adminPage/Table'
import {addEmptyData, isNoSelect, valueOrNoSelected} from '../../../../componentHelper/selectHelper'
import equal from 'fast-deep-equal'
import {SelectChangeEvent} from '@mui/material/Select'

const DEFAULT_QUANTITY_SUFFIX = '個'

export interface OptionSetting {
    localId: string
    optionId: Nullable<number>
    name: string
    sortOrder: number
    charge: number
    isFixedCharge: VALID_OR_NOT
    unitTimeHourValue: Nullable<number>
    unitTimeMinutesValue: Nullable<RESERVATION_TIME_UNIT_MINUTES_NOT0>
    unitTimeSuffix: Nullable<UNIT_TIME>
    minQuantity: number
    maxQuantity: number
    quantitySuffix: string
    paymentType: PAYMENT_TYPE
    required: VALID_OR_NOT
}

export interface FacilityOptionInputFormProps {
    stayAvailability: RENTAL_TYPE
    optionSetting: Nullable<OptionSetting>
    onSubmit: (optionSetting: OptionSetting) => void
    onCancel: () => void
}

interface State {
    name: string
    sortOrder: Nullable<number>
    charge: Nullable<number>
    isFixedCharge: boolean
    unitTimeHourValue: Nullable<number>
    unitTimeMinutesValue: Nullable<RESERVATION_TIME_UNIT_MINUTES_NOT0>
    unitTimeSuffix: UNIT_TIME
    minQuantity: Nullable<number>
    maxQuantity: Nullable<number>
    quantitySuffix: string
    paymentType: Nullable<PAYMENT_TYPE>
    required: boolean
    isClickedSubmitButton: boolean

    headingAreaMessageParams: Nullable<HeadingAreaMessageParams>
}

export default class FacilityOptionInputForm extends Component<FacilityOptionInputFormProps, State> {

    eventEmitter: EventEmitterExAdmin

    constructor(props: FacilityOptionInputFormProps) {
        super(props)

        this.eventEmitter = new EventEmitterExAdmin()

        this.state = {
            name: '',
            sortOrder: null,
            charge: null,
            isFixedCharge: props.stayAvailability === RENTAL_TYPE.OVERNIGHT,
            unitTimeHourValue: null,
            unitTimeMinutesValue: null,
            unitTimeSuffix: UNIT_TIME.MINUTES,
            minQuantity: 1,
            maxQuantity: null,
            quantitySuffix: '',
            paymentType: null,
            required: false,
            headingAreaMessageParams: null,
            isClickedSubmitButton: false,
        }

        if (props.optionSetting !== null) {
            const {isFixedCharge, required, unitTimeSuffix, ...rest} = props.optionSetting

            const fixCharge: boolean =
                (props.stayAvailability === RENTAL_TYPE.OVERNIGHT || isFixedCharge === VALID_OR_NOT.VALID)

            this.state = {
                headingAreaMessageParams: null,
                isFixedCharge: fixCharge,
                required: required === VALID_OR_NOT.VALID,
                unitTimeSuffix: unitTimeSuffix ? unitTimeSuffix : UNIT_TIME.HOUR,
                ...rest,
                isClickedSubmitButton: false,
            }
        }

        this.handleChangeSortOrder = this.handleChangeSortOrder.bind(this)
        this.handleChangeOptionName = this.handleChangeOptionName.bind(this)
        this.handleChangeFixedCharge = this.handleChangeFixedCharge.bind(this)
        this.handleChangeUnitTimeMinutesValue = this.handleChangeUnitTimeMinutesValue.bind(this)
        this.handleChangeUnitTimeHourValue = this.handleChangeUnitTimeHourValue.bind(this)
        this.handleChangeUnitTimeSuffix = this.handleChangeUnitTimeSuffix.bind(this)
        this.handleChangeCharge = this.handleChangeCharge.bind(this)
        this.handleChangeMinQuantity = this.handleChangeMinQuantity.bind(this)
        this.handleChangeMaxQuantity = this.handleChangeMaxQuantity.bind(this)
        this.handleChangeQuantitySuffix = this.handleChangeQuantitySuffix.bind(this)
        this.handleChangePaymentType = this.handleChangePaymentType.bind(this)
        this.handleChangeRequiredType = this.handleChangeRequiredType.bind(this)
        this.handleSubmitButtonClick = this.handleSubmitButtonClick.bind(this)
        this.handleCancelButtonClick = this.handleCancelButtonClick.bind(this)
    }

    componentDidMount(): void {
        this.validateFormDataAndShowErrors()
    }

    shouldComponentUpdate(nextProps: Readonly<FacilityOptionInputFormProps>, nextState: Readonly<State>): boolean {
        return !(
            (this.props === nextProps || equal(this.props, nextProps))
            && (this.state === nextState || equal(this.state, nextState))
        )
    }

    isEnableSubmitButton(): boolean {
        const {
            name,
            isFixedCharge,
            unitTimeSuffix,
            unitTimeHourValue,
            unitTimeMinutesValue,
            charge,
            minQuantity,
            maxQuantity,
            paymentType,
        } = this.state

        if (name === '') {
            return false
        }

        if (!isFixedCharge) {
            if (unitTimeSuffix === UNIT_TIME.HOUR && unitTimeHourValue === null) {
                return false
            }
            if (unitTimeSuffix === UNIT_TIME.MINUTES && unitTimeMinutesValue === null) {
                return false
            }
        }

        if (charge === null) {
            return false
        }

        if (minQuantity === null) {
            return false
        }

        if (maxQuantity === null) {
            return false
        }

        // noinspection RedundantIfStatementJS
        if (paymentType === null) {
            return false
        }

        return true
    }

    handleChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({sortOrder: numberOrNull(event.target.value)})
    }

    handleChangeOptionName(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({name: event.target.value})
    }

    handleChangeFixedCharge(label: string, checked: boolean): void {
        this.setState({isFixedCharge: checked})
    }

    handleChangeUnitTimeHourValue(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({unitTimeHourValue: numberOrNull(event.target.value)})
    }

    handleChangeUnitTimeMinutesValue(event: SelectChangeEvent<unknown>): void {
        const value: string = `${event.target.value}`
        this.setState({
            unitTimeMinutesValue: isNoSelect(value) ? null :
                RESERVATION_TIME_UNIT_MINUTES_NOT0.codeToEnumConstants(value),
        })
    }

    handleChangeUnitTimeSuffix(event: SelectChangeEvent<unknown>): void {
        const suffix = UNIT_TIME.codeToEnumConstants(event.target.value as string)
        this.setState({unitTimeSuffix: suffix})
    }

    handleChangeCharge(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({charge: numberOrNull(event.target.value)})
    }

    handleChangeMinQuantity(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({minQuantity: numberOrNull(event.target.value)})
    }

    handleChangeMaxQuantity(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({maxQuantity: numberOrNull(event.target.value)})
    }

    handleChangeQuantitySuffix(event: React.ChangeEvent<{ name?: string; value: unknown }>): void {
        this.setState({quantitySuffix: event.target.value as string})
    }

    handleChangePaymentType(event: SelectChangeEvent<unknown>): void {
        const value: string = `${event.target.value}`
        this.setState({paymentType: isNoSelect(value) ? null : PAYMENT_TYPE.codeToEnumConstants(value)})
    }

    handleChangeRequiredType(name: string, checked: boolean): void {
        this.setState({required: checked})
    }

    isValidQuantityPair(): boolean {
        const {minQuantity, maxQuantity} = this.state
        if (minQuantity && maxQuantity) {
            return !(minQuantity > maxQuantity)
        }
        return true
    }

    broadcastValidationError(
        titleMessage: Messages,
        titleMessageArguments: string[],
        detailMesge: Messages,
        detailMessageArguments: string[],
        requestItemErrors: RequestItemError[],
    ): void {
        const validateErrorMessageTitle: string = titleMessage.toString(...titleMessageArguments)
        const validateErrorMessageDetail: string = detailMesge.toString(...detailMessageArguments)

        this.setState({
            headingAreaMessageParams: {
                messageLevel: MESSAGE_TYPE.ERROR,
                title: validateErrorMessageTitle,
                detail: validateErrorMessageDetail,
            },
        })

        this.eventEmitter.broadcastErrors(
            new HttpError(HttpStatusCode.CLIENT_ERROR, new ErrorResponseBody(
                new RequestError(
                    titleMessage.id,
                    validateErrorMessageTitle,
                    validateErrorMessageDetail,
                    MESSAGE_TYPE.ERROR,
                ),
                requestItemErrors,
            )),
        )
    }

    validateFormDataAndShowErrors(): boolean {
        if (!this.isValidQuantityPair()) {
            this.broadcastValidationError(
                Messages.EOPCA0006, ['オプション'],
                Messages.EOPCA0004, [],
                [new RequestItemError(
                    'maxQuantityCheck', Messages.EOPCC1014.toString('最小数量'), Messages.EOPCC1014.id,
                )],
            )
            return false
        }

        return true
    }

    handleSubmitButtonClick(): void {
        if (!this.validateFormDataAndShowErrors()) {
            return
        }

        this.setState({isClickedSubmitButton: true})

        const quantitySuffix: string =
            (this.state.quantitySuffix.length === 0) ? DEFAULT_QUANTITY_SUFFIX : this.state.quantitySuffix
        const sortOrder: number =
            this.state.sortOrder || HKConstants.VMN.FAC.MAX_ORDER_OF_FACILITY_OPTION_LIST_DISPLAY
        const required: VALID_OR_NOT = this.state.required ? VALID_OR_NOT.VALID : VALID_OR_NOT.NOT

        const isFixedCharge: boolean = this.state.isFixedCharge

        const isFixedChargeValue = isFixedCharge ? VALID_OR_NOT.VALID : VALID_OR_NOT.NOT
        const unitTimeSuffixValue = isFixedCharge ? null : this.state.unitTimeSuffix
        const unitTimeHourValue = (unitTimeSuffixValue === UNIT_TIME.HOUR) ? this.state.unitTimeHourValue : null
        const unitTimeMinutesValue =
            (unitTimeSuffixValue === UNIT_TIME.MINUTES)
                ? this.state.unitTimeMinutesValue
                : null
        const optionId = this.props.optionSetting && this.props.optionSetting.optionId

        this.props.onSubmit({
                localId: createUUID(),
                optionId: optionId,
                name: this.state.name,
                sortOrder: sortOrder,
                charge: this.state.charge || 0,
                isFixedCharge: isFixedChargeValue,
                unitTimeHourValue: unitTimeHourValue,
                unitTimeMinutesValue: unitTimeMinutesValue,
                unitTimeSuffix: unitTimeSuffixValue,
                minQuantity: this.state.minQuantity || 0,
                maxQuantity: this.state.maxQuantity || 0,
                quantitySuffix: quantitySuffix,
                paymentType: this.state.paymentType || PAYMENT_TYPE.CASH,
                required: required,
            },
        )
    }

    handleCancelButtonClick(): void {
        this.props.onCancel()
    }

    renderFormTable(): ReactNode {
        return (
            <Table>
                <StyledTableCaption>オプション情報</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>並び順</TableRowName>
                        <TableCell>
                            <SortOrderInput
                                InputProps={{
                                    inputProps: {
                                        min: 1,
                                        max: HKConstants.VMN.FAC.MAX_ORDER_OF_FACILITY_OPTION_LIST_DISPLAY,
                                    },
                                    defaultValue: this.state.sortOrder,
                                }}
                                autoFocus
                                placeholder={`${HKConstants.VMN.FAC.MAX_ORDER_OF_FACILITY_OPTION_LIST_DISPLAY}`}
                                onChange={this.handleChangeSortOrder}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>名前</TableRowName>
                        <TableCell>
                            <NameInput
                                InputProps={{
                                    inputProps: {
                                        maxLength: HKConstants.VMN.FAC.MAX_LENGTH_OF_FACILITY_OPTION_NAME,
                                        defaultValue: this.state.name,
                                    },
                                }}
                                onChange={this.handleChangeOptionName}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>料金区分(固定/従量)</TableRowName>
                        <TableCell>
                            <PPCheckboxGroup
                                data={[{
                                    label: '固定料金',
                                    checked: this.state.isFixedCharge,
                                    checkboxProps: {
                                        disabled: this.props.stayAvailability === RENTAL_TYPE.OVERNIGHT,
                                    },
                                }]}
                                hint={Messages.HFACA0008.toString()}
                                onChangeChecked={this.handleChangeFixedCharge}
                            />
                            <LeftRowFlexContainer>
                                {
                                    this.state.unitTimeSuffix === UNIT_TIME.HOUR &&
                                    <HoursInput
                                        InputProps={{
                                            inputProps: {
                                                min: 1,
                                                max: HKConstants.VMN.FAC.MAX_PRICE_UNIT_TIME_HOUR,
                                            },
                                        }}
                                        value={this.state.unitTimeHourValue || ''}
                                        disabled={this.state.isFixedCharge}
                                        onChange={this.handleChangeUnitTimeHourValue}
                                    />
                                }
                                {
                                    this.state.unitTimeSuffix === UNIT_TIME.MINUTES &&
                                    <MinutesSelect
                                        data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES_NOT0.values())}
                                        value={valueOrNoSelected(this.state.unitTimeMinutesValue)}
                                        disabled={this.state.isFixedCharge}
                                        onChange={this.handleChangeUnitTimeMinutesValue}
                                    />
                                }
                                <Box ml={'16px'}>
                                    <PPSelect
                                        data={UNIT_TIME.values()}
                                        required
                                        value={this.state.unitTimeSuffix.value}
                                        disabled={this.state.isFixedCharge}
                                        onChange={this.handleChangeUnitTimeSuffix}
                                    />
                                </Box>
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>料金</TableRowName>
                        <TableCell>
                            <label>
                                <ChargeInput
                                    InputProps={{
                                        inputProps: {
                                            min: 0,
                                            max: HKConstants.VMN.FAC.MAX_PRICE,
                                        },
                                        defaultValue: this.state.charge,
                                        endAdornment: <InputAdornment position="end">円</InputAdornment>,
                                    }}
                                    required
                                    onChange={this.handleChangeCharge}
                                />
                            </label>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>数量</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                <label>
                                    <LeftRowFlexContainer>
                                        <NoWrapDiv>最小</NoWrapDiv>
                                        <QuantityInput
                                            InputProps={{
                                                inputProps: {
                                                    min: 1,
                                                    max: HKConstants.VMN.FAC.MAX_NUMBER_OF_OPTION_QUANTITY,
                                                },
                                                defaultValue: this.state.minQuantity,
                                            }}
                                            required
                                            onChange={this.handleChangeMinQuantity}
                                        />
                                    </LeftRowFlexContainer>
                                </label>
                                <Box ml={'16px'}>
                                    <label>
                                        <LeftRowFlexContainer>
                                            <NoWrapDiv>最大</NoWrapDiv>
                                            <QuantityInput
                                                InputProps={{
                                                    inputProps: {
                                                        min: 1,
                                                        max: HKConstants.VMN.FAC.MAX_NUMBER_OF_OPTION_QUANTITY,
                                                    },
                                                    defaultValue: this.state.maxQuantity,
                                                }}
                                                required
                                                onChange={this.handleChangeMaxQuantity}
                                                eventEmitter={this.eventEmitter}
                                                eventKeys={['maxQuantityCheck']}
                                            />
                                        </LeftRowFlexContainer>
                                    </label>
                                </Box>
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>単位</TableRowName>
                        <TableCell>
                            <QuantitySuffixInput
                                InputProps={{
                                    inputProps: {
                                        minLength: 0,
                                        maxLength: HKConstants.VMN.FAC.MAX_LENGTH_OF_FACILITY_OPTION_UNIT_NAME,
                                        placeholder: DEFAULT_QUANTITY_SUFFIX,
                                    },
                                    defaultValue: this.state.quantitySuffix,
                                }}
                                onChange={this.handleChangeQuantitySuffix}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>支払方法</TableRowName>
                        <TableCell>
                            <PPSelect
                                required
                                data={addEmptyData(PAYMENT_TYPE.values(), '支払方法を選択してください')}
                                value={valueOrNoSelected(this.state.paymentType)}
                                onChange={this.handleChangePaymentType}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>必須確認</TableRowName>
                        <TableCell>
                            <PPCheckboxGroup
                                data={[{
                                    label: '必須項目にする',
                                    checked: this.state.required,
                                }]}
                                hint={Messages.HFACA0009.toString()}
                                onChangeChecked={this.handleChangeRequiredType}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        )
    }

    render(): ReactNode {
        return <>
            <StyledDialogTitle>
                <AdminPageHeadingArea
                    title={'オプション設定'}
                />
                {
                    this.state.headingAreaMessageParams &&
                    <HeadingAreaMessageComponent params={this.state.headingAreaMessageParams}/>
                }
            </StyledDialogTitle>
            <StyledDialogContent>
                <PageMainArea>
                    <form>
                        {this.renderFormTable()}
                    </form>
                </PageMainArea>
            </StyledDialogContent>
            <StyledDialogActions>
                <PageFooterArea>
                    <PageFotterAreaRight>
                        <AdminPrimaryButton
                            onClick={this.handleSubmitButtonClick}
                            disabled={!this.isEnableSubmitButton() || this.state.isClickedSubmitButton}
                        >
                            登録
                        </AdminPrimaryButton>
                    </PageFotterAreaRight>
                    <PageFotterAreaLeft>
                        <BackLink onClick={this.handleCancelButtonClick}>キャンセル</BackLink>
                    </PageFotterAreaLeft>
                </PageFooterArea>
            </StyledDialogActions>

        </>
    }
}

const StyledDialogTitle = styled(DialogTitle)`
  padding-bottom: 0!important;
  padding-left: 12px!important;
  padding-right: 12px!important;
`

const StyledDialogContent = styled(DialogContent)`
  padding-left: 12px!important;
  padding-right: 12px!important;
`

const StyledDialogActions = styled(DialogActions)`
  padding-left: 12px!important;
  padding-right: 12px!important;
`

const NoWrapDiv = styled.div`
  white-space: nowrap;
`

const SortOrderInput = styled(PPNumericField)`
  width: 60px;
`

const NameInput = styled(PPTextField)`
  width: 310px;
`

const MinutesSelect = styled(PPSelect)`
  width: 60px;
`

const HoursInput = styled(PPNumericField)`
  width: 60px;
`

const ChargeInput = styled(PPNumericField)`
  width: 110px;
`

const QuantityInput = styled(PPNumericField)`
  width: 70px;
`

const QuantitySuffixInput = styled(PPTextField)`
  width: 200px;
`
